import React, { useEffect, useState } from 'react';
import ShipmentFilterApplySelector from './ShipmentFilterApplySelector';
import { NotificationApplyOptions } from '@/common/static_data';
import { NotificationFilterApplyEnum } from '@/common/enum';
import { getApplyConfig } from '@/pages/Settings/Notifications/NotificationConfig';
import { Flex, Form, FormInstance } from 'antd';
import { DeleteIcon } from '@/assets/icons/icons';
import { CustomShipmentText } from '@/components';
import { useTeamGroups } from '@/hooks/useTeamGroups';

type ApplyConfig = ReturnType<
  typeof getApplyConfig
>[NotificationFilterApplyEnum];

interface ShipmentFilterApplyProps {
  form: FormInstance;
  setApplyType: React.Dispatch<React.SetStateAction<number[]>>;
  applyType: number[];
  applyConfig: Record<NotificationFilterApplyEnum, ApplyConfig>;
  handleNodeUpdate?: (
    data: Record<string, unknown>,
    isSelectAll?: boolean
  ) => void;
  origins?: unknown[];
  originsLoading?: boolean;
  originsHasMore?: boolean;
  onLoadMoreOrigins?: () => void;
  products?: unknown[];
  productsLoading?: boolean;
  productsHasMore?: boolean;
  onLoadMoreProducts?: () => void;
  destinations?: unknown[];
  destinationsLoading?: boolean;
  destinationsHasMore?: boolean;
  onLoadMoreDestinations?: () => void;
  isReadOnly?: boolean;
}

const ShipmentFilterApply: React.FC<ShipmentFilterApplyProps> = ({
  form,
  setApplyType,
  applyType,
  applyConfig,
  handleNodeUpdate,
  originsLoading,
  originsHasMore,
  onLoadMoreOrigins,
  productsLoading,
  productsHasMore,
  onLoadMoreProducts,
  destinationsLoading,
  destinationsHasMore,
  onLoadMoreDestinations,
  isReadOnly = false,
}) => {
  const [open, setOpen] = useState(false);
  const [openState, setOpenState] = useState<Record<number, boolean>>({});
  const [allApplyType, setAllApplyType] = useState(false);
  // Team groups expand to their teams in the team filter.
  const teamCfg = (Object.values(applyConfig) as ApplyConfig[]).find(
    (cfg) => cfg?.key === 'filter_teams'
  );
  const teamGroups = useTeamGroups(
    (teamCfg?.list ?? []).flatMap((item) =>
      item.id === undefined ? [] : [item.id]
    ),
    !!teamCfg
  );

  useEffect(() => {
    const allCategoryTypesSelected =
      applyType.length === NotificationApplyOptions.length;
    if (!allCategoryTypesSelected) {
      setAllApplyType(false);
      return;
    }

    setAllApplyType(
      [
        'select_all_teams',
        'select_all_origins',
        'select_all_products',
        'select_all_transport_providers',
        'select_all_destinations',
      ].every((key) => Boolean(form.getFieldValue(key)))
    );
  }, [applyType, form]);

  // Accepts (string|number)[] to satisfy the selector's handleApplyFilters signature.
  // Converts to numbers internally before updating applyType state.
  const handleApplyTypes = (
    val: (string | number)[],
    isAllSelected = false
  ): void => {
    const types = val.map(Number);
    setApplyType(types);
    setAllApplyType(isAllSelected);
    setOpen(false);
    // For categories no longer in the active selection, clear their data key and stateKey.
    // If parent "Select All" was clicked, mark all categories as ALL.
    // Remaining active categories retain their current values/state instead of being wiped.
    (Object.entries(applyConfig) as [string, ApplyConfig][]).forEach(
      ([type, cfg]) => {
        const isIncluded = types.includes(Number(type));
        if (!isIncluded) {
          form.setFieldsValue({ [cfg.key]: [] });
          if (cfg.stateKey) {
            form.setFieldsValue({ [cfg.stateKey]: isAllSelected });
          }
        } else if (isAllSelected) {
          form.setFieldsValue({ [cfg.key]: [] });
          if (cfg.stateKey) {
            form.setFieldsValue({ [cfg.stateKey]: true });
          }
        }
      }
    );
  };

  const syncParentAllFromChildFilters = (): boolean => {
    const allCategoriesActive =
      applyType.length === NotificationApplyOptions.length;
    const allChildFiltersAreExplicitAll = (
      Object.values(applyConfig) as ApplyConfig[]
    ).every((cfg) => Boolean(form.getFieldValue(cfg.stateKey)));
    const nextAllApplyType =
      allCategoriesActive && allChildFiltersAreExplicitAll;

    setAllApplyType(nextAllApplyType);
    return nextAllApplyType;
  };

  const handleApplyFilters = (
    val: React.ChangeEvent<HTMLInputElement> | (string | number)[],
    cfg: ApplyConfig,
    isAllSelected = false
  ): void => {
    const value = (val as React.ChangeEvent<HTMLInputElement>)?.target
      ? (val as React.ChangeEvent<HTMLInputElement>).target.value
      : val;
    form.setFieldsValue({ [cfg.key]: isAllSelected ? [] : value });
    // Persist stateKey so it survives across multiple selector updates
    if (cfg.stateKey) {
      form.setFieldsValue({ [cfg.stateKey]: isAllSelected });
    }
    const nextAllApplyType = syncParentAllFromChildFilters();
    if (handleNodeUpdate) {
      handleNodeUpdate(form.getFieldsValue(true), nextAllApplyType);
    }
  };

  // Sync node when applyType changes
  useEffect(() => {
    if (handleNodeUpdate) {
      handleNodeUpdate(form.getFieldsValue(true), allApplyType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyType, allApplyType]);

  return (
    <>
      <Flex vertical gap={16}>
        <div>
          <CustomShipmentText
            text={'Which shipments does this apply to'}
            fontSize='14px'
            marginBottom='4px'
            fontWeight={600}
          />
          <ShipmentFilterApplySelector
            applyType={applyType}
            isParernt={true}
            setOpen={setOpen}
            open={open}
            list={NotificationApplyOptions}
            titleText=''
            border='1px solid var(--filter-tag-border)'
            background='var(--surface-container)'
            color='var(--filter-tag-text)'
            handleApplyFilters={handleApplyTypes}
            patchedIsAll={allApplyType}
            disabled={isReadOnly}
          />
        </div>
        {!allApplyType && (
          <>
            {applyType?.map((type) => {
              const cfg = applyConfig[type as NotificationFilterApplyEnum];
              if (!cfg) return null;

              const listData = cfg.list;
              let hasMore = false;
              let loading = false;
              let onLoadMore: (() => void) | undefined;

              switch (type) {
                case NotificationFilterApplyEnum.Origin:
                  hasMore = originsHasMore ?? false;
                  loading = originsLoading ?? false;
                  onLoadMore = onLoadMoreOrigins;
                  break;
                case NotificationFilterApplyEnum.Product:
                  hasMore = productsHasMore ?? false;
                  loading = productsLoading ?? false;
                  onLoadMore = onLoadMoreProducts;
                  break;
                case NotificationFilterApplyEnum.Destination:
                  hasMore = destinationsHasMore ?? false;
                  loading = destinationsLoading ?? false;
                  onLoadMore = onLoadMoreDestinations;
                  break;
                default:
                  break;
              }

              return (
                <Form.Item
                  key={type}
                  className='custom-input form-input-style custom-select-disabled-only'
                  label={cfg.label}
                  name={cfg.key}
                  getValueFromEvent={(val) => val}
                  style={{ flex: 1 }}
                >
                  <Flex align='center' gap={16}>
                    <ShipmentFilterApplySelector
                      formName={cfg.key}
                      form={form}
                      patchedArray={form.getFieldValue(cfg.key) ?? []}
                      patchedIsAll={form.getFieldValue(cfg.stateKey) ?? false}
                      isParernt={false}
                      open={openState[type] ?? false}
                      setOpen={(val) =>
                        setOpenState((prev) => ({
                          ...prev,
                          [type]:
                            typeof val === 'function'
                              ? val(prev[type] ?? false)
                              : val,
                        }))
                      }
                      list={listData}
                      titleText={cfg.label}
                      border='0px'
                      background='rgba(94, 94, 94, 1)'
                      color='white'
                      handleApplyFilters={(val, isAllSelected) => {
                        handleApplyFilters(val, cfg, isAllSelected);
                        setOpenState((prev) => ({ ...prev, [type]: false }));
                      }}
                      applyCourierId={cfg.applyCourierId}
                      hasMore={hasMore}
                      loading={loading}
                      onLoadMore={onLoadMore}
                      disabled={isReadOnly}
                      teamGroups={
                        cfg.key === 'filter_teams' ? teamGroups : undefined
                      }
                    />
                    {!isReadOnly && (
                      <div>
                        <DeleteIcon
                          color='var(--text-secondary)'
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setApplyType((prev) =>
                              prev.filter((v) => v !== type)
                            );
                            form.setFieldsValue({
                              [cfg.key]: [],
                              [cfg.stateKey]: false,
                            });
                          }}
                        />
                      </div>
                    )}
                  </Flex>
                </Form.Item>
              );
            })}
          </>
        )}
      </Flex>
    </>
  );
};

export default ShipmentFilterApply;
